import { useEffect, useState, useMemo, useCallback, type ReactNode } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { type ColumnDef, type ColumnFiltersState, type SortingState } from "@tanstack/react-table";
import { Card } from "@/ui/atoms/Card";
import { Button, Loader, Modal } from "@/ui/atoms";
import { DataTable } from "@/ui/molecules";
import { ScrollText, ExternalLink, Download } from "lucide-react";
import { formatDateTimeEuropeBerlin } from "@/utils/europeBerlinTime";
import { routerPaths } from "@/ui/router/routerPaths";
import type { TAsyncState } from "@/store";
import { AuditDataDiff } from "./AuditDataDiff";
import { AuditLogExportModal, type TAuditLogExportRequest } from "./AuditLogExportModal";

export type TAuditLogEntry = {
    id: number;
    eventAt: string;
    /** DB table (global audit); company audit maps this to "". */
    tableName: string;
    action: string;
    rowId: string | null;
    companyId: string | null;
    companyName: string | null;
    userId: string | null;
    userName: string | null;
    userEmail: string | null;
    emulatedSubgroup: string | null;
    oldData: Record<string, unknown> | null;
    newData: Record<string, unknown> | null;
    changedFields: string[] | null;
    // Human-friendly fields (company endpoint only)
    description: string | null;
    category: string | null;
    changes: { field: string; before: string | null; after: string | null }[] | null;
    // File/deal context columns
    dealName: string | null;
    serviceProviderName: string | null;
    period: string | null;
    fileId: string | null;
    fileName: string | null;
};

export type TAuditLogPage = {
    items: TAuditLogEntry[];
    total: number;
    totalUnfiltered: number;
    /** The server counts only up to a cap; when set, the total is a lower bound. */
    totalCapped?: boolean;
    totalUnfilteredCapped?: boolean;
    page: number;
    pageSize: number;
    hasMore: boolean;
};

export type TAuditLogFacetOptions = {
    deals: { id: string; name: string }[];
    businessPartners: { id: string; name: string }[];
    periods: string[];
    users: { id: string; name: string }[];
    companies: { id: string; name: string }[];
};

export type TViewAuditLogParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useAuditLogState: () => TAsyncState<TAuditLogPage>;
        onPageChange: (page: number) => void;
        currentPage: () => number;
        isGlobal: boolean;
        subgroupLabels: Record<string, string>;
        onDownloadFile: (fileId: string) => Promise<void>;
        // Server-mode table handles
        columnFilters: ColumnFiltersState;
        onColumnFiltersChange: (filters: ColumnFiltersState) => void;
        sorting: SortingState;
        onSortingChange: (sorting: SortingState) => void;
        facetOptions: TAuditLogFacetOptions;
        /** Product-Owner-only Excel export (company view). */
        canExport: boolean;
        onExport: (request: TAuditLogExportRequest) => Promise<void>;
    };
};

export type TViewAuditLogProps = Record<string, never>;

const ACTION_COLORS: Record<string, string> = {
    INSERT: "bg-green-100 text-green-800",
    UPDATE: "bg-blue-100 text-blue-800",
    DELETE: "bg-red-100 text-red-800",
};

const AUDIT_ACTION_FILTER_OPTIONS = [
    { value: "INSERT", label: "INSERT" },
    { value: "UPDATE", label: "UPDATE" },
    { value: "DELETE", label: "DELETE" },
];

export const AUDIT_CATEGORY_FILTER_OPTIONS = [
    { value: "Files", label: "Files" },
    { value: "Deals", label: "Deals" },
    { value: "Business Partners", label: "Business Partners" },
    { value: "Users", label: "Users" },
    { value: "Companies", label: "Companies" },
    { value: "Chart of Accounts", label: "Chart of Accounts" },
    { value: "Property Codes", label: "Property Codes" },
    { value: "Segment Values", label: "Segment Values" },
    { value: "Segment Mappings", label: "Segment Mappings" },
    { value: "Property Code Mappings", label: "Property Code Mappings" },
    { value: "Supporting Files", label: "Supporting Files" },
    { value: "Books", label: "Books" },
    { value: "Account Mappings", label: "Account Mappings" },
    { value: "Column Mappings", label: "Column Mappings" },
    { value: "Announcements", label: "Announcements" },
];

export const resolveActingAs = (labels: Record<string, string>, sg: string): string =>
    labels[sg] ?? sg;

/** One label/value row in the audit detail modal's metadata grid. */
const DetailRow = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className="grid grid-cols-[130px_1fr] gap-3">
        <dt className="text-xs font-semibold text-muted-foreground pt-0.5">{label}</dt>
        <dd className="text-sm break-words min-w-0">{children}</dd>
    </div>
);

export const createViewAuditLog = ({ useCases }: TViewAuditLogParams) => {
    return function ViewAuditLog() {
        const {
            onMount,
            onUnmount,
            useAuditLogState,
            onPageChange,
            currentPage,
            isGlobal,
            subgroupLabels,
            onDownloadFile,
            columnFilters,
            onColumnFiltersChange,
            sorting,
            onSortingChange,
            facetOptions,
            canExport,
            onExport,
        } = useCases();

        const auditLogState = useAuditLogState();
        const page = currentPage();

        useEffect(() => {
            onMount();
            return () => onUnmount();
        }, [onMount, onUnmount]);

        // Modal state for viewing row data
        const [selectedEntry, setSelectedEntry] = useState<TAuditLogEntry | null>(null);
        const [isExportOpen, setIsExportOpen] = useState(false);
        const showExport = !isGlobal && canExport;

        const data = auditLogState.data;
        const items = useMemo(() => data?.items ?? [], [data]);

        // Facet-backed filter options (server-side: values are ids)
        const filterOptions = useMemo(
            () => ({
                submissionGroup: facetOptions.deals.map((d) => ({
                    value: d.id,
                    label: d.name,
                })),
                businessPartner: facetOptions.businessPartners.map((b) => ({
                    value: b.id,
                    label: b.name,
                })),
                period: facetOptions.periods.map((p) => ({ value: p, label: p })),
                changedBy: facetOptions.users.map((u) => ({
                    value: u.id,
                    label: u.name,
                })),
                company: facetOptions.companies.map((c) => ({
                    value: c.id,
                    label: c.name,
                })),
            }),
            [facetOptions],
        );

        const handleRowClick = useCallback((entry: TAuditLogEntry) => {
            setSelectedEntry(entry);
        }, []);

        const globalColumns: ColumnDef<TAuditLogEntry>[] = useMemo(
            () => [
                {
                    id: "eventAt",
                    accessorKey: "eventAt",
                    header: "Time (CET)",
                    meta: { filterType: "date" as const },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTimeEuropeBerlin(row.original.eventAt)}
                        </span>
                    ),
                    size: 160,
                },
                {
                    accessorKey: "action",
                    header: "Action",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: AUDIT_ACTION_FILTER_OPTIONS,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const action = row.original.action;
                        return (
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[action] || "bg-gray-100 text-gray-800"}`}
                            >
                                {action}
                            </span>
                        );
                    },
                    size: 80,
                },
                {
                    accessorKey: "tableName",
                    header: "Table",
                    meta: {
                        filterType: "text" as const,
                        filterPlaceholder: "Filter table…",
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => (
                        <span className="text-sm font-mono">{row.original.tableName}</span>
                    ),
                    size: 200,
                },
                {
                    id: "changedFields",
                    accessorFn: (row) =>
                        row.changedFields?.length ? row.changedFields.join(", ") : "",
                    header: "Changed Fields",
                    enableSorting: false,
                    meta: {
                        filterType: "text" as const,
                        filterPlaceholder: "Filter fields…",
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fields = row.original.changedFields;
                        if (!fields || fields.length === 0)
                            return <span className="text-muted-foreground">-</span>;
                        return (
                            <span
                                className="text-xs font-mono text-muted-foreground truncate block max-w-[200px]"
                                title={fields.join(", ")}
                            >
                                {fields.join(", ")}
                            </span>
                        );
                    },
                    size: 200,
                },
                {
                    id: "rowId",
                    accessorFn: (row) => row.rowId ?? "",
                    header: "Row ID",
                    enableSorting: false,
                    meta: {
                        filterType: "text" as const,
                        filterPlaceholder: "Filter row ID…",
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const id = row.original.rowId;
                        if (!id) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span
                                className="text-xs font-mono truncate block max-w-[120px]"
                                title={id}
                            >
                                {id.slice(0, 8)}...
                            </span>
                        );
                    },
                    size: 120,
                },
                {
                    id: "company_id",
                    accessorFn: (row) =>
                        [row.companyName, row.companyId].filter(Boolean).join(" ") || "",
                    header: "Company",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.company,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const name = row.original.companyName;
                        const cid = row.original.companyId;
                        if (!name && !cid) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span
                                className="text-sm truncate block max-w-[150px]"
                                title={cid || undefined}
                            >
                                {name || cid?.slice(0, 8) + "..."}
                            </span>
                        );
                    },
                    size: 150,
                },
                {
                    id: "deal_id",
                    accessorFn: (row) => row.dealName ?? "",
                    header: "Submission group",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.submissionGroup,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const deal = row.original.dealName;
                        if (!deal) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span className="text-sm truncate block max-w-[150px]" title={deal}>
                                {deal}
                            </span>
                        );
                    },
                    size: 150,
                },
                {
                    id: "service_provider_id",
                    accessorFn: (row) => row.serviceProviderName ?? "",
                    header: "Business Partner",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.businessPartner,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const sp = row.original.serviceProviderName;
                        if (!sp) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span className="text-sm truncate block max-w-[150px]" title={sp}>
                                {sp}
                            </span>
                        );
                    },
                    size: 150,
                },
                {
                    id: "period",
                    accessorFn: (row) => row.period ?? "",
                    header: "Period",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.period,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const period = row.original.period;
                        if (!period) return <span className="text-muted-foreground">-</span>;
                        return <span className="text-sm whitespace-nowrap">{period}</span>;
                    },
                    size: 100,
                },
                {
                    id: "file",
                    accessorFn: (row) => [row.fileName, row.fileId].filter(Boolean).join(" ") || "",
                    header: "File",
                    enableColumnFilter: false,
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fileId = row.original.fileId;
                        const fileName = row.original.fileName;
                        if (!fileId && !fileName)
                            return <span className="text-muted-foreground">-</span>;
                        if (!fileId) {
                            return (
                                <span
                                    className="text-sm truncate block max-w-[150px]"
                                    title={fileName || undefined}
                                >
                                    {fileName}
                                </span>
                            );
                        }
                        return (
                            <a
                                href={routerPaths.files.detail(fileId)}
                                className="text-sm text-primary hover:underline truncate block max-w-[150px] inline-flex items-center gap-1"
                                title={fileName || fileId}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {fileName || "View file"}
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                        );
                    },
                    size: 160,
                },
                {
                    accessorKey: "emulatedSubgroup",
                    header: "Acting as",
                    cell: ({ row }) => {
                        const sg = row.original.emulatedSubgroup;
                        if (!sg) return <span className="text-gray-400">—</span>;
                        return <span>{resolveActingAs(subgroupLabels, sg)}</span>;
                    },
                    size: 120,
                },
                {
                    id: "user_id",
                    accessorFn: (row) =>
                        [row.userName, row.userEmail].filter(Boolean).join(" ") || "",
                    header: "Changed By",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.changedBy,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const name = row.original.userName;
                        const email = row.original.userEmail;
                        if (!name && !email) {
                            return (
                                <span className="text-xs text-muted-foreground italic">System</span>
                            );
                        }
                        return (
                            <span
                                className="text-sm truncate block max-w-[160px]"
                                title={email || undefined}
                            >
                                {name || email}
                            </span>
                        );
                    },
                    size: 160,
                },
                {
                    id: "downloads",
                    header: "Downloads",
                    enableColumnFilter: false,
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fileId = row.original.fileId;
                        if (!fileId) return <span className="text-muted-foreground">-</span>;
                        return (
                            <button
                                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDownloadFile(fileId);
                                }}
                            >
                                Generated file
                                <Download className="h-3 w-3 flex-shrink-0" />
                            </button>
                        );
                    },
                    size: 140,
                },
            ],
            [onDownloadFile, subgroupLabels, filterOptions],
        );

        const companyColumns: ColumnDef<TAuditLogEntry>[] = useMemo(
            () => [
                {
                    id: "eventAt",
                    accessorKey: "eventAt",
                    header: "Time (CET)",
                    meta: { filterType: "date" as const },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => (
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTimeEuropeBerlin(row.original.eventAt)}
                        </span>
                    ),
                    size: 160,
                },
                {
                    accessorKey: "action",
                    header: "Action",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: AUDIT_ACTION_FILTER_OPTIONS,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const action = row.original.action;
                        return (
                            <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[action] || "bg-gray-100 text-gray-800"}`}
                            >
                                {action}
                            </span>
                        );
                    },
                    size: 80,
                },
                {
                    id: "category",
                    accessorFn: (row) => row.category ?? "",
                    header: "Category",
                    enableSorting: false,
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: AUDIT_CATEGORY_FILTER_OPTIONS,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => (
                        <span className="text-sm font-medium">{row.original.category || "-"}</span>
                    ),
                    size: 160,
                },
                {
                    id: "deal_id",
                    accessorFn: (row) => row.dealName ?? "",
                    header: "Submission group",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.submissionGroup,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const deal = row.original.dealName;
                        if (!deal) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span className="text-sm truncate block max-w-[150px]" title={deal}>
                                {deal}
                            </span>
                        );
                    },
                    size: 150,
                },
                {
                    id: "service_provider_id",
                    accessorFn: (row) => row.serviceProviderName ?? "",
                    header: "Business Partner",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.businessPartner,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const sp = row.original.serviceProviderName;
                        if (!sp) return <span className="text-muted-foreground">-</span>;
                        return (
                            <span className="text-sm truncate block max-w-[150px]" title={sp}>
                                {sp}
                            </span>
                        );
                    },
                    size: 150,
                },
                {
                    id: "period",
                    accessorFn: (row) => row.period ?? "",
                    header: "Period",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.period,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const period = row.original.period;
                        if (!period) return <span className="text-muted-foreground">-</span>;
                        return <span className="text-sm whitespace-nowrap">{period}</span>;
                    },
                    size: 100,
                },
                {
                    id: "file",
                    accessorFn: (row) => [row.fileName, row.fileId].filter(Boolean).join(" ") || "",
                    header: "File",
                    enableColumnFilter: false,
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fileId = row.original.fileId;
                        const fileName = row.original.fileName;
                        if (!fileId && !fileName)
                            return <span className="text-muted-foreground">-</span>;
                        if (!fileId) {
                            return (
                                <span
                                    className="text-sm truncate block max-w-[150px]"
                                    title={fileName || undefined}
                                >
                                    {fileName}
                                </span>
                            );
                        }
                        return (
                            <a
                                href={routerPaths.files.detail(fileId)}
                                className="text-sm text-primary hover:underline truncate block max-w-[150px] inline-flex items-center gap-1"
                                title={fileName || fileId}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {fileName || "View file"}
                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                        );
                    },
                    size: 160,
                },
                {
                    accessorKey: "emulatedSubgroup",
                    header: "Acting as",
                    cell: ({ row }) => {
                        const sg = row.original.emulatedSubgroup;
                        if (!sg) return <span className="text-gray-400">—</span>;
                        return <span>{resolveActingAs(subgroupLabels, sg)}</span>;
                    },
                    size: 120,
                },
                {
                    id: "user_id",
                    accessorFn: (row) =>
                        [row.userName, row.userEmail].filter(Boolean).join(" ") || "",
                    header: "Changed By",
                    meta: {
                        filterType: "multiSelect" as const,
                        filterOptions: filterOptions.changedBy,
                        filterSearchable: true,
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const name = row.original.userName;
                        const email = row.original.userEmail;
                        if (!name && !email) {
                            return (
                                <span className="text-xs text-muted-foreground italic">System</span>
                            );
                        }
                        return (
                            <span
                                className="text-sm truncate block max-w-[160px]"
                                title={email || undefined}
                            >
                                {name || email}
                            </span>
                        );
                    },
                    size: 160,
                },
                {
                    id: "description",
                    accessorFn: (row) => row.description ?? "",
                    header: "Description",
                    // Filtering is intentionally not offered (generated, high-cardinality
                    // text); the column remains for at-a-glance readability.
                    enableColumnFilter: false,
                    enableSorting: false,
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => (
                        <span
                            className="text-sm text-foreground truncate block max-w-[300px]"
                            title={row.original.description || ""}
                        >
                            {row.original.description || "-"}
                        </span>
                    ),
                    size: 300,
                },
                {
                    id: "changedFields",
                    accessorFn: (row) =>
                        row.changedFields?.length ? row.changedFields.join(", ") : "",
                    header: "Details",
                    enableSorting: false,
                    meta: {
                        filterType: "text" as const,
                        filterPlaceholder: "Filter details…",
                    },
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fields = row.original.changedFields;
                        if (!fields || fields.length === 0)
                            return <span className="text-muted-foreground">-</span>;
                        return (
                            <div className="flex flex-wrap gap-1 max-w-[250px]">
                                {fields.slice(0, 3).map((f) => (
                                    <span
                                        key={f}
                                        className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded"
                                    >
                                        {f}
                                    </span>
                                ))}
                                {fields.length > 3 && (
                                    <span
                                        className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                                        title={fields.join(", ")}
                                    >
                                        +{fields.length - 3} more
                                    </span>
                                )}
                            </div>
                        );
                    },
                    size: 250,
                },
                {
                    id: "downloads",
                    header: "Downloads",
                    enableColumnFilter: false,
                    cell: ({ row }: { row: { original: TAuditLogEntry } }) => {
                        const fileId = row.original.fileId;
                        if (!fileId) return <span className="text-muted-foreground">-</span>;
                        return (
                            <button
                                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDownloadFile(fileId);
                                }}
                            >
                                Generated file
                                <Download className="h-3 w-3 flex-shrink-0" />
                            </button>
                        );
                    },
                    size: 140,
                },
            ],
            [onDownloadFile, subgroupLabels, filterOptions],
        );

        const columns = isGlobal ? globalColumns : companyColumns;

        const total = data?.total ?? 0;
        const pageSize = data?.pageSize ?? 50;

        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <ScrollText className="h-6 w-6 text-primary" />
                        <div>
                            <h1 className="text-2xl font-bold">
                                {isGlobal ? "Global Audit Log" : "Audit Log"}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {isGlobal
                                    ? "All changes across all companies"
                                    : "All changes within your company"}
                            </p>
                        </div>
                    </div>
                    {showExport && (
                        <Button
                            variant="outline"
                            data-testid="audit-log-export"
                            onClick={() => setIsExportOpen(true)}
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download Excel
                        </Button>
                    )}
                </div>

                {/* Content */}
                <Card>
                    {auditLogState.status === "pending" && !auditLogState.data ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader />
                        </div>
                    ) : auditLogState.status === "rejected" ? (
                        <div className="p-6 text-center text-red-600">
                            <p>Failed to load audit log</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                {auditLogState.error?.message}
                            </p>
                        </div>
                    ) : items.length === 0 &&
                      auditLogState.status !== "pending" &&
                      columnFilters.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                            <ScrollText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No audit log entries yet</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            storageKey="audit-log"
                            data={items}
                            manualFiltering
                            manualSorting
                            sortingState={sorting}
                            onSortingChange={onSortingChange}
                            onColumnFiltersChange={onColumnFiltersChange}
                            initialColumnFilters={columnFilters}
                            rowCount={buildRowCount({
                                total: total,
                                totalUnfiltered: data?.totalUnfiltered,
                                hasActiveFilters: columnFilters.length > 0,
                                totalCapped: data?.totalCapped,
                                totalUnfilteredCapped: data?.totalUnfilteredCapped,
                            })}
                            rowNoun={{ one: "entry", many: "entries" }}
                            pagination={{
                                page,
                                pageSize,
                                total,
                                onPageChange,
                                totalIsLowerBound: data?.totalCapped ?? false,
                            }}
                            onRowClick={handleRowClick}
                        />
                    )}
                </Card>

                {showExport && isExportOpen && (
                    <AuditLogExportModal
                        open={true}
                        onOpenChange={setIsExportOpen}
                        onExport={onExport}
                        categoryOptions={AUDIT_CATEGORY_FILTER_OPTIONS}
                    />
                )}

                {/* Detail Modal */}
                {selectedEntry && (
                    <Modal
                        className={isGlobal ? "max-w-4xl" : undefined}
                        open={true}
                        onOpenChange={(open) => {
                            if (!open) setSelectedEntry(null);
                        }}
                        title={
                            isGlobal
                                ? `${selectedEntry.action} on ${selectedEntry.tableName}`
                                : selectedEntry.description || `${selectedEntry.action}`
                        }
                    >
                        <div className="space-y-4 max-h-[60vh] overflow-auto">
                            {/* Metadata grid — full context for the selected entry */}
                            <dl className="space-y-2 border-b pb-4">
                                <DetailRow label="Time (CET)">
                                    {formatDateTimeEuropeBerlin(selectedEntry.eventAt)}
                                </DetailRow>
                                <DetailRow label="Action">
                                    <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[selectedEntry.action] || "bg-gray-100 text-gray-800"}`}
                                    >
                                        {selectedEntry.action}
                                    </span>
                                </DetailRow>
                                {isGlobal && selectedEntry.tableName && (
                                    <DetailRow label="Table">
                                        <span className="font-mono text-sm">
                                            {selectedEntry.tableName}
                                        </span>
                                    </DetailRow>
                                )}
                                {!isGlobal && selectedEntry.category && (
                                    <DetailRow label="Category">{selectedEntry.category}</DetailRow>
                                )}
                                {isGlobal &&
                                    (selectedEntry.companyName || selectedEntry.companyId) && (
                                        <DetailRow label="Company">
                                            <span title={selectedEntry.companyId || undefined}>
                                                {selectedEntry.companyName ||
                                                    selectedEntry.companyId}
                                            </span>
                                        </DetailRow>
                                    )}
                                {selectedEntry.dealName && (
                                    <DetailRow label="Submission group">
                                        {selectedEntry.dealName}
                                    </DetailRow>
                                )}
                                {selectedEntry.serviceProviderName && (
                                    <DetailRow label="Business Partner">
                                        {selectedEntry.serviceProviderName}
                                    </DetailRow>
                                )}
                                {selectedEntry.period && (
                                    <DetailRow label="Period">{selectedEntry.period}</DetailRow>
                                )}
                                {(selectedEntry.fileName || selectedEntry.fileId) && (
                                    <DetailRow label="File">
                                        {selectedEntry.fileId ? (
                                            <a
                                                href={routerPaths.files.detail(
                                                    selectedEntry.fileId,
                                                )}
                                                className="text-primary hover:underline inline-flex items-center gap-1"
                                                title={
                                                    selectedEntry.fileName || selectedEntry.fileId
                                                }
                                            >
                                                {selectedEntry.fileName || "View file"}
                                                <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                            </a>
                                        ) : (
                                            selectedEntry.fileName
                                        )}
                                    </DetailRow>
                                )}
                                {selectedEntry.fileId && (
                                    <DetailRow label="Downloads">
                                        <button
                                            className="text-primary hover:underline inline-flex items-center gap-1"
                                            onClick={() => onDownloadFile(selectedEntry.fileId!)}
                                        >
                                            Generated file
                                            <Download className="h-3 w-3 flex-shrink-0" />
                                        </button>
                                    </DetailRow>
                                )}
                                {selectedEntry.emulatedSubgroup && (
                                    <DetailRow label="Acting as">
                                        {resolveActingAs(
                                            subgroupLabels,
                                            selectedEntry.emulatedSubgroup,
                                        )}
                                    </DetailRow>
                                )}
                                <DetailRow label="Changed By">
                                    {selectedEntry.userName || selectedEntry.userEmail ? (
                                        <span title={selectedEntry.userEmail || undefined}>
                                            {selectedEntry.userName || selectedEntry.userEmail}
                                            {selectedEntry.userName && selectedEntry.userEmail && (
                                                <span className="text-muted-foreground ml-1">
                                                    ({selectedEntry.userEmail})
                                                </span>
                                            )}
                                        </span>
                                    ) : (
                                        <span className="italic text-muted-foreground">System</span>
                                    )}
                                </DetailRow>
                                {isGlobal && selectedEntry.rowId && (
                                    <DetailRow label="Row ID">
                                        <span className="font-mono text-xs break-all">
                                            {selectedEntry.rowId}
                                        </span>
                                    </DetailRow>
                                )}
                            </dl>

                            {/* Before/After table (company view) */}
                            {!isGlobal &&
                                selectedEntry.changes &&
                                selectedEntry.changes.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-muted-foreground mb-2">
                                            {selectedEntry.action === "UPDATE"
                                                ? "Changes"
                                                : "Details"}
                                        </p>
                                        <div className="border rounded overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-muted/50">
                                                        <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">
                                                            Field
                                                        </th>
                                                        {selectedEntry.action !== "INSERT" && (
                                                            <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">
                                                                Before
                                                            </th>
                                                        )}
                                                        {selectedEntry.action !== "DELETE" && (
                                                            <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">
                                                                {selectedEntry.action === "INSERT"
                                                                    ? "Value"
                                                                    : "After"}
                                                            </th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedEntry.changes.map((c, i) => (
                                                        <tr
                                                            key={i}
                                                            className="border-t border-muted even:bg-muted/40"
                                                        >
                                                            <td className="px-3 py-1.5 font-medium text-xs">
                                                                {c.field}
                                                            </td>
                                                            {selectedEntry.action !== "INSERT" && (
                                                                <td
                                                                    className={`px-3 py-1.5 text-xs max-w-[240px] whitespace-pre-wrap break-words ${c.before !== null ? "bg-red-50 text-red-900 dark:bg-red-950/60 dark:text-red-200" : "text-muted-foreground"}`}
                                                                >
                                                                    {c.before ?? (
                                                                        <span className="italic">
                                                                            empty
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            )}
                                                            {selectedEntry.action !== "DELETE" && (
                                                                <td
                                                                    className={`px-3 py-1.5 text-xs max-w-[240px] whitespace-pre-wrap break-words ${c.after !== null ? "bg-green-50 text-green-900 dark:bg-green-950/60 dark:text-green-200" : ""}`}
                                                                >
                                                                    {c.after ?? (
                                                                        <span className="italic text-muted-foreground">
                                                                            empty
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                            {/* Coloured old/new diff (global view only) */}
                            {isGlobal && (
                                <AuditDataDiff
                                    oldData={selectedEntry.oldData}
                                    newData={selectedEntry.newData}
                                />
                            )}
                        </div>
                    </Modal>
                )}
            </div>
        );
    };
};
